/**
 * presenceClient — multiplayer "who's live" presence over tile-pubsub-tunnel.
 *
 * The tunnel (tile-pubsub-tunnel, gateway-proxied at /pubsub) is a generic
 * Socket.IO channel broker. It already gives us server-authoritative join AND
 * leave for free:
 *   - on `subscribeChannel` it broadcasts `joinChannel {channel, sender}` to
 *     everyone else in the room,
 *   - on `disconnecting` (tab close / crash / network drop) OR
 *     `unsubscribeChannel` it broadcasts `leaveChannel {channel, sender}`.
 * So we need no heartbeat and no backend change — presence rides its own
 * channel namespace `presence:app:<appId>`, isolated from the preview traffic
 * that shares the same tunnel.
 *
 * Because join/leave events only carry the opaque `sender` string, display
 * fields (name, photo) are exchanged with a `HELLO` message. A late joiner
 * learns existing peers because each peer replies to the joiner's
 * `joinChannel` with its own `HELLO`.
 *
 * This module is intentionally React-free (mirrors ./openContext.ts). UI reads
 * it via `subscribePresence`; see PresenceStack.tsx for the `usePresence` hook.
 */
import { io, type Socket } from 'socket.io-client';

export interface PresenceProfile {
  /** Stable user id — used as the channel `sender` and roster key. */
  id: string;
  name: string;
  photo: string | null;
}

/** Cursor position as fractions (0..1) of the viewport — resolution-independent
 * so it lines up across differently-sized windows. */
export interface CursorPos {
  x: number;
  y: number;
}

/** What a peer is currently looking at — the awareness ("follow") plane. Pure
 * ephemeral, last-write-wins, no reconcile. `screen` = the app screen derived
 * from the open file (e.g. "Checkout"); `file` = the raw open file path. Either
 * may be null (e.g. editing a non-screen file, or not resolved yet). */
export interface FocusState {
  screen: string | null;
  file: string | null;
}

export interface PresencePeer {
  profile: PresenceProfile;
  /** True for the local user. */
  isSelf: boolean;
  /** True if this peer edited within ACTIVE_WINDOW_MS. */
  active: boolean;
  /** Stable per-user color for avatar ring + cursor. */
  color: string;
  /** Latest cursor position (fractions of viewport), or null if stale/hidden. */
  cursor: CursorPos | null;
  /** True if this peer has a live co-editing session open (drives "Join"). */
  live: boolean;
  /** What this peer is currently editing (screen + file) — awareness plane. */
  focus: FocusState;
}

/** Wire envelope carried inside the tunnel's free-form `message` payload. */
type PresenceMsg =
  | { t: 'HELLO'; profile: PresenceProfile; live?: boolean; focus?: FocusState }
  | { t: 'ACTIVITY' }
  | { t: 'CURSOR'; x: number; y: number } // x<0 ⇒ hide (pointer left the window)
  | { t: 'FOCUS'; screen: string | null; file: string | null };

/** A peer is rendered "active" (green) if it edited within this window. */
const ACTIVE_WINDOW_MS = 5000;
/** Don't flood the channel while someone types — cap ACTIVITY to this cadence. */
const ACTIVITY_THROTTLE_MS = 2500;
/** Cursor send cadence — 50ms ≈ 20/s, smooth with CSS interpolation on receive. */
const CURSOR_THROTTLE_MS = 50;
/** Hide a peer's cursor if we haven't heard a move in this long. */
const CURSOR_STALE_MS = 4000;
/** Re-announce cadence. Kept short so a live connection always re-announces
 *  within LEAVE_GRACE_MS below (that's what makes leave-grace safe), and so a
 *  peer that missed our discovery HELLO recovers fast. Still tiny traffic. */
const HELLO_INTERVAL_MS = 2000;
/** A `leaveChannel` is NOT applied immediately — see the handler. We wait this
 *  long and cancel the removal if we hear anything from the peer, because the
 *  tunnel keys leave by userId and fires it on ANY disconnect (StrictMode
 *  remount, a second tab, a socket.io reconnect), which would otherwise evict a
 *  peer that still has a live connection. Must exceed HELLO_INTERVAL_MS so a
 *  genuinely-live peer's re-announce always lands inside the window. */
const LEAVE_GRACE_MS = 2600;
/** After connect, re-send HELLO on this short ramp so discovery converges
 *  sub-second instead of waiting a full HELLO_INTERVAL_MS when a join/reply
 *  message is dropped in the initial handshake race. */
const HELLO_BURST_MS = [150, 600, 1400];
/** Debounce teardown so React StrictMode / HMR remounts (connect → cleanup →
 *  connect in the same tick) don't churn the socket and emit a spurious leave. */
const DISCONNECT_DEBOUNCE_MS = 500;
/** Socket.IO path: gateway rewrites /pubsub/(.*) → /$1 onto the tunnel. */
const PUBSUB_PATH = '/pubsub/socket.io';

/** Distinct, high-contrast cursor/ring colors; assigned stably per user id. */
const PALETTE = [
  '#e5484d',
  '#e93d82',
  '#8e4ec6',
  '#3e63dd',
  '#0091ff',
  '#12a594',
  '#30a46c',
  '#f76b15',
];
function colorFor(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length];
}

interface RosterEntry {
  profile: PresenceProfile;
  lastActive: number;
  isSelf: boolean;
  cursor: CursorPos | null;
  cursorTs: number;
  live: boolean;
  focus: FocusState;
}

const NO_FOCUS: FocusState = { screen: null, file: null };

type Listener = (peers: PresencePeer[]) => void;

const listeners = new Set<Listener>();
const roster = new Map<string, RosterEntry>();

let socket: Socket | null = null;
let channel: string | null = null;
let self: PresenceProfile | null = null;
let selfLive = false;
let selfFocus: FocusState = NO_FOCUS;
let lastFocusSent = '';
let lastActivitySent = 0;
let lastCursorSent = 0;
let decayTimer: ReturnType<typeof setInterval> | null = null;
let helloTimer: ReturnType<typeof setInterval> | null = null;
let pendingDisconnect: ReturnType<typeof setTimeout> | null = null;
/** Per-sender pending leave-removal timers (grace window). Cancelled the moment
 *  we hear from that sender again — see handleMessage + the leaveChannel handler. */
const pendingLeaves = new Map<string, ReturnType<typeof setTimeout>>();

function clearPendingLeaves(): void {
  for (const t of pendingLeaves.values()) clearTimeout(t);
  pendingLeaves.clear();
}

// ── DEBUG instrumentation (remove once presence discovery is fixed) ───────────
// Flip DEBUG off (or delete this block) when done. Logs are prefixed with WHO we
// are + our current socket.id so two clients are easy to tell apart in one glance.
const DEBUG = false;
function shortId(v: string | null | undefined): string {
  return v ? String(v).slice(0, 6) : '------';
}
function plog(tag: string, ...args: unknown[]): void {
  if (!DEBUG) return;
  const me = self ? `${self.name}#${shortId(self.id)}` : 'none';
  const sid = socket?.id ? shortId(socket.id) : '----';
  const conn = socket?.connected ? 'up' : 'down';
  // eslint-disable-next-line no-console
  console.log(
    `%c[multiplayer]%c ${me} sock:${sid}/${conn} — ${tag}`,
    'color:#fff;background:#8e4ec6;font-weight:700;padding:1px 4px;border-radius:3px',
    'color:inherit',
    ...args,
  );
}
function rosterIds(): string[] {
  return [...roster.values()].map(
    (e) => `${e.profile.name}#${shortId(e.profile.id)}${e.isSelf ? '(self)' : ''}`,
  );
}
if (typeof window !== 'undefined') {
  (window as unknown as { __presenceDebug?: () => void }).__presenceDebug = () => {
    // eslint-disable-next-line no-console
    console.log('[multiplayer] STATE', {
      self,
      channel,
      selfLive,
      socketId: socket?.id ?? null,
      socketConnected: socket?.connected ?? false,
      transport: (socket as unknown as { io?: { engine?: { transport?: { name?: string } } } })?.io
        ?.engine?.transport?.name,
      rosterSize: roster.size,
      roster: rosterIds(),
      pendingLeaves: [...pendingLeaves.keys()].map(shortId),
    });
  };
}

function snapshot(): PresencePeer[] {
  const now = Date.now();
  const peers: PresencePeer[] = [];
  for (const entry of roster.values()) {
    peers.push({
      profile: entry.profile,
      isSelf: entry.isSelf,
      active: now - entry.lastActive < ACTIVE_WINDOW_MS,
      color: colorFor(entry.profile.id),
      cursor: entry.cursor && now - entry.cursorTs < CURSOR_STALE_MS ? entry.cursor : null,
      live: entry.live,
      focus: entry.focus,
    });
  }
  // Self first, then active-before-idle, then by name for a stable order.
  peers.sort((a, b) => {
    if (a.isSelf !== b.isSelf) return a.isSelf ? -1 : 1;
    if (a.active !== b.active) return a.active ? -1 : 1;
    return a.profile.name.localeCompare(b.profile.name);
  });
  return peers;
}

let lastRosterSig = '';
function emit(): void {
  const peers = snapshot();
  // Log only when the set of peers CHANGES (emit also fires every 1s to decay
  // "active", which we don't want to spam).
  const sig = peers
    .map((p) => `${shortId(p.profile.id)}:${p.active ? 'a' : 'i'}:${p.live ? 'L' : ''}`)
    .join(',');
  if (sig !== lastRosterSig) {
    lastRosterSig = sig;
    plog(
      `📋 roster now (${peers.length})`,
      peers.map((p) => `${p.profile.name}#${shortId(p.profile.id)}${p.isSelf ? '(self)' : ''}`),
    );
  }
  for (const l of listeners) {
    try {
      l(peers);
    } catch {
      /* one listener can't break the others */
    }
  }
}

function send(message: PresenceMsg): void {
  if (!socket || !channel || !self) return;
  // Tunnel relays `message` to everyone else in the room (never back to us).
  socket.emit('message', { channel, sender: self.id, message });
}

function sendHello(): void {
  if (!self) return;
  // `live` and `focus` ride HELLO (incl. the 5s re-announce) so late joiners and
  // recovered peers learn our session state + what we're editing without a
  // separate message.
  plog('→ HELLO broadcast', {
    live: selfLive,
    focus: selfFocus,
    canSend: !!(socket && channel && self),
  });
  send({ t: 'HELLO', profile: self, live: selfLive, focus: selfFocus });
}

/**
 * Set whether the local user has a live co-editing session open. Broadcasts via
 * HELLO so peers can show a "Join" affordance. See collabClient for the session.
 */
export function setLive(live: boolean): void {
  selfLive = live;
  if (self) {
    const mine = roster.get(self.id);
    if (mine) mine.live = live;
  }
  sendHello();
  emit();
}

/**
 * Broadcast what the local user is currently editing — the awareness ("follow")
 * plane. Safe to call on every file switch; de-duped so an unchanged focus never
 * re-broadcasts. Ephemeral + last-write-wins, exactly like CURSOR — it does NOT
 * touch the co-editing/reconcile path. The value is captured even before the
 * socket connects, so it rides the first HELLO on connect.
 */
export function sendFocus(screen: string | null, file: string | null): void {
  selfFocus = { screen: screen ?? null, file: file ?? null };
  if (self) {
    const mine = roster.get(self.id);
    if (mine) mine.focus = selfFocus;
  }
  const key = `${selfFocus.screen} ${selfFocus.file}`;
  if (key === lastFocusSent) {
    emit(); // local self-label still reflects the (unchanged) value
    return;
  }
  lastFocusSent = key;
  send({ t: 'FOCUS', screen: selfFocus.screen, file: selfFocus.file });
  emit();
}

function handleMessage(sender: string, message: unknown): void {
  const msg = message as PresenceMsg;
  if (!msg || typeof msg !== 'object') return;
  if (msg.t !== 'CURSOR')
    plog(
      `← ${msg.t} from ${shortId(sender)}`,
      msg.t === 'HELLO'
        ? { profile: (msg as { profile?: PresenceProfile }).profile?.name }
        : undefined,
    );
  // Any inbound message proves this sender still has a live connection → cancel
  // a pending grace-leave (a stale disconnect from another connection of theirs).
  const pl = pendingLeaves.get(sender);
  if (pl) {
    clearTimeout(pl);
    pendingLeaves.delete(sender);
    plog(`cancelled pending-leave for ${shortId(sender)} (heard from them again)`);
  }
  if (msg.t === 'HELLO') {
    if (!msg.profile || !msg.profile.id) return;
    const existing = roster.get(sender);
    roster.set(sender, {
      profile: msg.profile,
      // Preserve a fresher lastActive if we already had one.
      lastActive: Math.max(existing?.lastActive ?? 0, Date.now()),
      isSelf: false,
      cursor: existing?.cursor ?? null,
      cursorTs: existing?.cursorTs ?? 0,
      live: msg.live ?? false,
      // HELLO carries focus (incl. the 5s re-announce); fall back to any focus we
      // already learned via a FOCUS message so a HELLO can't blank it.
      focus: msg.focus ?? existing?.focus ?? NO_FOCUS,
    });
    // Newly-learned peer → answer with our own HELLO so they learn us too.
    // Covers the race where our join-reply HELLO was dropped in transit. Only
    // on first sight (not on every HELLO) so a pair exchanges a bounded few
    // messages, never a storm.
    if (!existing) {
      plog(
        `✅ LEARNED NEW PEER ${(msg.profile as PresenceProfile).name}#${shortId(
          sender,
        )} — replying HELLO. roster=`,
        rosterIds(),
      );
      sendHello();
    }
    emit();
  } else if (msg.t === 'ACTIVITY') {
    const entry = roster.get(sender);
    if (entry) {
      entry.lastActive = Date.now();
      emit();
    }
  } else if (msg.t === 'CURSOR') {
    const entry = roster.get(sender);
    if (entry) {
      // x<0 is the explicit "hide" sentinel (pointer left the window).
      entry.cursor = msg.x < 0 ? null : { x: msg.x, y: msg.y };
      entry.cursorTs = Date.now();
      emit();
    }
  } else if (msg.t === 'FOCUS') {
    const entry = roster.get(sender);
    if (entry) {
      entry.focus = { screen: msg.screen ?? null, file: msg.file ?? null };
      emit();
    }
  }
}

/**
 * Connect to presence for `appId` as `profile`. Idempotent: re-calling with the
 * same app + user is a no-op; switching apps tears down the old channel first.
 */
export function connectPresence(appId: string, profile: PresenceProfile): void {
  if (!appId || !profile?.id) return;
  // A StrictMode / HMR remount fires cleanup (which debounces a teardown) and
  // then immediately reconnects. Cancel that pending teardown so the live socket
  // is reused instead of being torn down and re-created (which would emit a
  // spurious leave and churn discovery).
  if (pendingDisconnect) {
    clearTimeout(pendingDisconnect);
    pendingDisconnect = null;
  }
  const nextChannel = `presence:app:${appId}`;
  if (socket && channel === nextChannel && self?.id === profile.id) {
    plog('connectPresence → no-op reuse (already connected, same app+user)');
    return;
  }
  if (socket) {
    plog('connectPresence → switching app/user, tearing down old socket first');
    teardown(); // switching app/user → tear down NOW, not debounced
  }

  self = profile;
  channel = nextChannel;
  roster.clear();
  // Show ourselves immediately so the stack is never empty. `selfFocus` may
  // already be set (a file switch that raced ahead of connect) — seed it so the
  // first HELLO carries it.
  lastFocusSent = '';
  roster.set(profile.id, {
    profile,
    lastActive: Date.now(),
    isSelf: true,
    cursor: null,
    cursorTs: 0,
    live: selfLive,
    focus: selfFocus,
  });

  socket = io(window.location.origin, {
    path: PUBSUB_PATH,
    transports: ['websocket', 'polling'],
    withCredentials: true,
  });

  plog('connectPresence → creating socket', {
    channel: nextChannel,
    alreadyConnected: socket.connected,
  });

  const onConnect = () => {
    if (!socket || !channel || !self) return;
    // (Re)subscribe on every connect so reconnects re-announce us. The tunnel
    // stores `sender` on the socket and uses it for the leave broadcast, so it
    // MUST be set for leave-on-disconnect to fire — AND for `socket.join(channel)`
    // so we actually RECEIVE the room's broadcasts (peers' HELLOs). Without this,
    // we can still emit into the room but never hear anyone → blind.
    plog('🔌 CONNECT — emitting subscribeChannel', {
      channel,
      transport: socket.io?.engine?.transport?.name,
      recovered: (socket as unknown as { recovered?: boolean }).recovered,
    });
    socket.emit('subscribeChannel', { channel, sender: self.id });
    sendHello();
    // Discovery burst — a dropped join/reply HELLO in the initial handshake
    // would otherwise leave one side blind until the next re-announce. Re-send
    // on a short ramp so a fresh pair converges sub-second. sendHello no-ops
    // after teardown (guards on `self`), so stray fires are harmless.
    for (const d of HELLO_BURST_MS) setTimeout(sendHello, d);
  };
  socket.on('connect', onConnect);
  // CRITICAL: socket.io's `connect` event does NOT fire if the socket is ALREADY
  // connected when we attach the listener. That happens whenever `io()` hands back
  // a REUSED, already-open connection — the tunnel socket is multiplexed with the
  // SDK/collab, so a client that already had it open never fires `connect` here,
  // never emits subscribeChannel, and thus never `join`s the presence room. It
  // then broadcasts HELLOs into a room it isn't a member of (peers see it, but it
  // sees no one) — the exact "first client stays blind" bug. So if we're already
  // connected, run the subscribe path right now instead of waiting for an event
  // that will never come.
  if (socket.connected) {
    plog('⚡ socket was ALREADY connected when handler attached (reused socket) — subscribing NOW');
    onConnect();
  }

  // Socket-level lifecycle — the smoking gun for "emitting into a room I left".
  socket.on('disconnect', (reason: string) => {
    plog('❌ DISCONNECT', {
      reason,
      note: 'server-side rooms are now dropped until the next connect re-subscribes',
    });
  });
  socket.io?.on('reconnect_attempt', (n: number) => plog('… reconnect_attempt', n));
  socket.io?.on('reconnect', (n: number) =>
    plog(
      '🔁 RECONNECT (NEW socket.id server-side → left all rooms; connect handler must re-subscribe)',
      { attempt: n },
    ),
  );
  socket.io?.on('error', (e: Error) => plog('io error', e?.message));
  socket.io?.engine?.on('upgrade', () =>
    plog('transport upgraded →', socket?.io?.engine?.transport?.name),
  );

  // This socket is SHARED with collabClient (which subscribes it to the
  // code:app channel too), so every handler must ignore events for other
  // channels — otherwise a code-channel join/leave would corrupt the roster.
  // A newcomer arrived after us → they don't know our profile yet. Reply with
  // HELLO. We only reply to joinChannel (never to HELLO) so there's no storm.
  socket.on('joinChannel', (data: { channel?: string; sender?: string }) => {
    if (data?.channel && data.channel !== channel) {
      plog(`(ignored joinChannel for other channel ${data.channel})`);
      return;
    }
    plog(`🚪 joinChannel: ${shortId(data?.sender)} joined my room → replying HELLO`);
    sendHello();
  });

  socket.on('message', (data: { channel?: string; sender: string; message: unknown }) => {
    if (data?.channel && data.channel !== channel) return; // other channel (code plane etc.)
    if (data?.sender === self?.id) {
      plog(`(ignored my own echo from ${shortId(data.sender)})`);
      return; // ignore our own echoes (defensive)
    }
    handleMessage(data.sender, data.message);
  });

  socket.on('leaveChannel', (data: { channel?: string; sender: string }) => {
    if (data?.channel && data.channel !== channel) return;
    const s = data?.sender;
    plog(`👋 leaveChannel: ${shortId(s)}`, {
      known: roster.has(s),
      alreadyPending: pendingLeaves.has(s),
    });
    if (!s || !roster.has(s) || pendingLeaves.has(s)) return;
    // Grace-verified removal. The tunnel keys leave by userId and fires it on
    // ANY disconnect (StrictMode remount, a second tab, a socket.io reconnect),
    // so an immediate delete would evict a peer that still has a live
    // connection. Defer, and cancel in handleMessage the instant we hear from
    // them again — a live peer re-announces well inside LEAVE_GRACE_MS.
    const timer = setTimeout(() => {
      pendingLeaves.delete(s);
      if (roster.delete(s)) {
        plog(`🗑️ grace expired → REMOVED ${shortId(s)}. roster=`, rosterIds());
        emit();
      }
    }, LEAVE_GRACE_MS);
    pendingLeaves.set(s, timer);
  });

  socket.on('connect_error', (err: Error) => {
    plog('⚠️ connect_error', err?.message);
  });

  socket.on('terminate', (reason: string) => {
    // Rate-limit / abuse termination from the tunnel. Presence load is tiny, so
    // this shouldn't happen under normal use — surface it if it does.
    // eslint-disable-next-line no-console
    console.warn('[presence] terminated by tunnel:', reason);
  });

  // Re-render once a second so "active" decays to idle without a new event.
  decayTimer = setInterval(emit, 1000);
  // Periodically re-announce presence. Discovery normally converges instantly
  // via the join-reply handshake, but a single dropped HELLO (transient relay
  // race) would otherwise hide a peer indefinitely — this heals it within one
  // interval. It's also what keeps leave-grace safe: a live peer re-announcing
  // every HELLO_INTERVAL_MS always cancels a stale grace-leave before it fires.
  helloTimer = setInterval(sendHello, HELLO_INTERVAL_MS);
  emit();
}

/** Actually tear the connection down. Synchronous — used directly on app/user
 *  switch; the public `disconnectPresence` debounces a call to this. */
function teardown(): void {
  plog('🧨 teardown() — unsubscribing + disconnecting socket, clearing roster');
  if (decayTimer) {
    clearInterval(decayTimer);
    decayTimer = null;
  }
  if (helloTimer) {
    clearInterval(helloTimer);
    helloTimer = null;
  }
  clearPendingLeaves();
  if (socket) {
    try {
      if (channel && self) socket.emit('unsubscribeChannel', { channel, sender: self.id });
    } catch {
      /* best-effort */
    }
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
  }
  channel = null;
  self = null;
  roster.clear();
  emit();
}

/**
 * Tear down the connection (call on unmount / app switch / logout). Debounced:
 * React StrictMode and HMR remount the editor with a synchronous
 * cleanup→re-mount, so we wait DISCONNECT_DEBOUNCE_MS before actually
 * disconnecting; `connectPresence` cancels the pending teardown if it reconnects
 * with the same identity, avoiding a spurious leave + discovery churn.
 */
export function disconnectPresence(): void {
  plog('disconnectPresence() called → debounced teardown scheduled', {
    ms: DISCONNECT_DEBOUNCE_MS,
  });
  if (pendingDisconnect) clearTimeout(pendingDisconnect);
  pendingDisconnect = setTimeout(() => {
    pendingDisconnect = null;
    teardown();
  }, DISCONNECT_DEBOUNCE_MS);
}

/**
 * Signal that the local user is actively editing. Throttled — safe to call on
 * every keystroke / pointer interaction. Bumps our own "active" state and tells
 * peers.
 */
export function markLocalActivity(): void {
  if (!self) return;
  const now = Date.now();
  const mine = roster.get(self.id);
  if (mine) mine.lastActive = now;
  if (now - lastActivitySent < ACTIVITY_THROTTLE_MS) {
    emit(); // local self-highlight still updates immediately
    return;
  }
  lastActivitySent = now;
  send({ t: 'ACTIVITY' });
  emit();
}

/**
 * Broadcast the local cursor position (viewport fractions 0..1). Throttled to
 * CURSOR_THROTTLE_MS. Pass a negative `x` to hide (pointer left the window).
 * Pure awareness — does NOT count as edit activity.
 */
export function sendCursor(x: number, y: number): void {
  if (!self) return;
  const now = Date.now();
  const hide = x < 0;
  if (!hide && now - lastCursorSent < CURSOR_THROTTLE_MS) return;
  lastCursorSent = now;
  send({ t: 'CURSOR', x, y });
}

/**
 * The live tunnel socket. Shared with collabClient so the editing plane rides
 * the SAME connection (the tunnel is channel-based — one socket, many rooms),
 * instead of opening a second connection to the same origin+path (which
 * socket.io multiplexes into a fragile shared socket). Null until presence
 * connects (i.e. before the editor mounts with an appId + identity).
 */
export function getTunnelSocket(): Socket | null {
  return socket;
}

/** The presence channel currently subscribed (so collab can co-exist on the same socket). */
export function getPresenceChannel(): string | null {
  return channel;
}

/** Subscribe to roster changes. Fires once immediately with the current list. */
export function subscribePresence(listener: Listener): () => void {
  listeners.add(listener);
  try {
    listener(snapshot());
  } catch {
    /* swallow */
  }
  return () => {
    listeners.delete(listener);
  };
}
